import React, { useState } from 'react';
import { Zap, ChevronDown, ChevronUp, Hash, Calendar, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function InsightBlock({ insights, topicSlug, showViewAllLink = false }) {
  const [expandedId, setExpandedId] = useState(null);

  if (!insights || insights.length === 0) {
    return null;
  }

  return (
    <section id="insights" className="scroll-mt-24">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Zap className="w-6 h-6 text-yellow-500" />
          Key Insights
        </h2>
      </div>

      <div className="space-y-4">
        {insights.map((insight) => (
          <article 
            key={insight.id}
            className="bg-surface border border-white/10 rounded-xl overflow-hidden hover:border-primary/30 transition-all"
          >
            <button
              onClick={() => setExpandedId(expandedId === insight.id ? null : insight.id)}
              className="w-full text-left p-5 flex items-start justify-between gap-4 group"
            >
              <div className="flex-1">
                <h3 className="font-semibold text-white text-lg group-hover:text-primary transition-colors pr-4">
                  {insight.title}
                </h3>
                {expandedId !== insight.id && (
                  <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                    {insight.hook || insight.summary?.substring(0, 150)}...
                  </p>
                )}
              </div>
              <div className="flex-shrink-0 text-slate-500 group-hover:text-primary transition-colors">
                {expandedId === insight.id ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </div>
            </button>

            {expandedId === insight.id && (
              <div className="px-5 pb-5 border-t border-white/5 pt-4 space-y-6">
                
                {/* Hook */}
                {insight.hook && (
                  <p className="text-lg text-slate-300 leading-relaxed">{insight.hook}</p>
                )}

                {/* Topic Tag + Date */}
                <div className="flex flex-wrap gap-3">
                  {insight.topics && (
                    <Link 
                      to={`/topics/${insight.topics.slug || topicSlug}`} 
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Hash className="w-3 h-3" /> {insight.topics.title}
                    </Link>
                  )}
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface border border-white/10 text-slate-400 text-sm">
                    <Calendar className="w-3 h-3" /> {new Date(insight.created_at).toLocaleDateString()}
                  </span>
                  
                  {/* Brand Attribution */}
                  {insight.projects && (
                    <Link
                      to={`/brands/${insight.projects.slug}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface border border-white/10 text-slate-400 text-sm hover:text-primary hover:border-primary/30 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {insight.projects.logo_url ? (
                        <img 
                          src={insight.projects.logo_url} 
                          alt={insight.projects.name} 
                          className="w-4 h-4 rounded-full object-cover"
                        />
                      ) : (
                        <span className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                          {insight.projects.name?.charAt(0)}
                        </span>
                      )}
                      {insight.projects.name}
                    </Link>
                  )}
                </div>

                {/* The Insight (Body) */}
                {insight.body && (
                  <section>
                    <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                      <span className="w-1 h-6 bg-primary rounded-full"/> The Insight
                    </h4>
                    <div className="prose prose-invert max-w-none">
                      <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{insight.body}</p>
                    </div>
                  </section>
                )}

                {/* The Takeaway */}
                {insight.takeaway && (
                  <section className="bg-surface/30 p-6 rounded-xl border border-white/5 relative">
                    <Quote className="w-8 h-8 text-slate-600 absolute top-4 right-4 opacity-20" />
                    <h4 className="text-sm font-bold text-slate-400 uppercase mb-2">The Takeaway</h4>
                    <p className="italic text-slate-300 text-lg font-medium">{insight.takeaway}</p>
                  </section>
                )}

              </div>
            )}
          </article>
        ))}
      </div>

      {showViewAllLink && insights.length > 5 && (
        <div className="mt-6 text-center">
          <Link
            to={`/topics/${topicSlug}#insights`}
            className="inline-flex items-center gap-2 text-primary hover:text-primary-hover font-medium transition-colors"
          >
            View all insights
            <ChevronDown className="w-4 h-4" />
          </Link>
        </div>
      )}
    </section>
  );
}
